import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "./Button";
import { msalInstance } from "../../msalInstance";
import { apiScopes, loginRequest } from "../../authConfig";
import { setReauthHandler } from "../../utils/apiFetch";

// Asks the person to sign in again when the session can no longer be renewed silently.
// It is a popup sign-in: the page is NOT reloaded, so anything being edited stays as it is,
// and the API call that was waiting continues by itself once the new token arrives.
// It renders nothing unless utils/apiFetch asks for it.

const isMockAuth = process.env.REACT_APP_MOCK_AUTH === "true";
const POPUP_BLOCKED = ["popup_window_error", "empty_window_error"];

export default function SessionExpiredModal() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(null);

  useEffect(() => {
    if (isMockAuth) return undefined;
    const unregister = setReauthHandler(
      () =>
        new Promise((resolve) => {
          if (pending.current) pending.current(false);
          pending.current = resolve;
          setError("");
          setBusy(false);
          setOpen(true);
        })
    );
    return () => {
      unregister();
      if (pending.current) {
        pending.current(false);
        pending.current = null;
      }
    };
  }, []);

  const finish = useCallback((signedIn) => {
    const resolve = pending.current;
    pending.current = null;
    setOpen(false);
    setBusy(false);
    setError("");
    if (resolve) resolve(signedIn);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) finish(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, finish]);

  const signIn = async () => {
    setBusy(true);
    setError("");
    try {
      const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
      const result = await msalInstance.acquireTokenPopup({
        scopes: apiScopes,
        account,
        // A blank page, so the whole app is not loaded inside the popup.
        redirectUri: `${window.location.origin}/blank.html`,
      });
      if (account && result?.account && result.account.homeAccountId !== account.homeAccountId) {
        setBusy(false);
        setError("wrongAccount");
        return;
      }
      finish(true);
    } catch (err) {
      setBusy(false);
      const code = err?.errorCode || "";
      setError(POPUP_BLOCKED.includes(code) ? "popupBlocked" : code === "user_cancelled" ? "cancelled" : "failed");
    }
  };

  // Last resort: a normal full-page sign-in. It reloads the page, so unsaved changes are lost.
  const fullPageSignIn = () => {
    msalInstance.loginRedirect({ ...loginRequest }).catch(() => setError("failed"));
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
        className="bg-white rounded-xl p-6 w-full max-w-[480px] shadow-xl mx-4"
      >
        <h3 id="session-expired-title" className="text-lg font-bold text-black-800 mb-2">
          {t("session.title")}
        </h3>
        <p className="text-black-600 mb-4">{t("session.message")}</p>

        {error && (
          <div className="mb-4">
            <p role="alert" className="text-red-600 text-sm">
              {t(`session.${error}`)}
            </p>
            <button
              type="button"
              onClick={fullPageSignIn}
              className="mt-2 text-sm underline text-black-600 hover:text-black-900"
            >
              {t("session.fullPage")}
            </button>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <Button variant="ghost" type="button" onClick={() => finish(false)} disabled={busy}>
            {t("session.notNow")}
          </Button>
          <Button variant="primary" type="button" onClick={signIn} disabled={busy} autoFocus>
            {busy ? t("session.signingIn") : t("session.signIn")}
          </Button>
        </div>
      </div>
    </div>
  );
}
